<template>
  <div :class="wrapperClasses" data-test="hero-banner">
    <ImageRenderer
      v-if="image"
      :alt="image?.alt"
      class="flex h-full justify-center"
      :class="{
        'max-h-[744px] min-h-[584px]': isBigBanner,
        'absolute w-full': !isBigBanner,
      }"
      :height="image?.height"
      :height-mobile="image?.heightMobile"
      :loading="Loading.Eager"
      :src="image?.src"
      :src-mobile="image?.srcMobile"
      :width="image?.width"
      :width-mobile="image?.widthMobile"
    />

    <RevContainer
      class="bottom-0 w-full pb-32 lg:pb-72"
      :class="
        isBigBanner
          ? 'absolute pt-[6.5rem]'
          : 'relative md:absolute pt-[5.5rem]'
      "
    >
      <div
        v-if="isBigBanner && gradientEnabled"
        class="absolute -bottom-4 left-0 h-full w-[101%]"
        :class="hasWhiteText ? $style.blackBlur : $style.whiteBlur"
      />

      <div
        v-if="gradientEnabled"
        class="absolute inset-0"
        :class="hasWhiteText ? $style.blackGradient : $style.whiteGradient"
        data-test="hero-banner-overlay"
      />

      <div class="relative" :class="{ 'text-center': align === 'center' }">
        <div class="inline-flex flex-col md:max-w-[736px]">
          <Heading :badge :heading-type :size="titleSize" :text-color :title />

          <RichText
            v-if="subtitle"
            class="mt-4"
            :class="[
              getBannerTextColorClass(textColor),
              relatedLinks ? 'line-clamp-3 md:line-clamp-5' : '',
            ]"
            :rich-text="subtitle"
            style-variant="none"
          />
        </div>

        <RelatedLinks
          v-if="relatedLinks"
          :align="relatedLinks.align"
          class="mt-32"
          :display="relatedLinks.display"
          :links="relatedLinks.links"
          :size="relatedLinks.size"
        />
      </div>
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import {
  type Banner,
  BannerTextColor,
} from '@backmarket/http-api/src/api-specs-content/models/banner'
import type { BasePageContentAPI } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import type { RichText as RichTextType } from '@backmarket/http-api/src/api-specs-content/models/rich-text'
import { Loading } from '@backmarket/nuxt-layer-cms/ImageRenderer.constants'
import ImageRenderer from '@backmarket/nuxt-layer-cms/ImageRenderer.vue'
import RelatedLinks from '@backmarket/nuxt-layer-cms/RelatedLinks.vue'
import RichText from '@backmarket/nuxt-layer-cms/RichText.vue'
import { tw } from '@backmarket/utils/string/tw'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevContainer } from '@ds/components/Container'

import { getBannerTextColorClass } from '../../helpers/banner/getTextColor'
import Heading from '../Heading/Heading.vue'

type BannerProps = Partial<
  Banner & {
    badge?: Maybe<string>
    title?: Maybe<string>
    titleSize: Banner['titleSize']
    align: BasePageContentAPI['align']
    subtitle?: Maybe<RichTextType>
    headingType: 'h1' | 'p'
    gradientEnabled?: Maybe<boolean>
  }
>

const {
  image = undefined,
  textColor = BannerTextColor.Black,
  subtitle = undefined,
  titleSize = 'large',
  align = 'left',
  title,
  headingType,
  gradientEnabled = true,
} = defineProps<BannerProps>()

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error'])

const hasWhiteText = computed(() => {
  return textColor === BannerTextColor.White
})

const isBigBanner = computed(() => {
  return image && image.height === 1168
})

const wrapperClasses = computed(() => {
  return [
    tw`relative m-auto overflow-hidden max-w-[2688px]`,
    !isBigBanner.value && tw`md:aspect-[16/5] lg:aspect-[16/4]`,
    isBigBanner.value &&
      tw`aspect-[1440/584] max-h-[744px] min-h-[584px] w-full object-contain`,
  ]
})
</script>

<style module>
.blackBlur {
  backdrop-filter: blur(6px);
  background: linear-gradient(180deg, transparent, rgba(15, 17, 23, 0.85));
  filter: blur(6px);
  mask-image: linear-gradient(0deg, #000000 0%, transparent);
}

.blackGradient {
  opacity: 0.3;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 1) 120%);
}

.whiteBlur {
  backdrop-filter: blur(6px);
  background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.85));
  filter: blur(6px);
  mask-image: linear-gradient(0deg, #000000 0%, transparent);
}

.whiteGradient {
  opacity: 0.3;
  background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 1) 120%);
}
</style>
